import { useState, useRef, useEffect } from "react"
import { PathLayer } from "@deck.gl/layers"
import { PathStyleExtension } from "@deck.gl/extensions"
import type { PickingInfo } from "@deck.gl/core"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { getGlobalMapRef } from "features/map/GlobalViewState"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { selectOrthoComparison, setOrthoComparisonThreshold } from "../OrthoComparisonSlice"

// DeckGL reads getCursor outside React's render cycle. Getter ensures callers always see the current value.
let _comparisonHoverCursor: string | null = null
export const getComparisonHoverCursor = () => _comparisonHoverCursor

export function useOrthoComparisonDivider(): PathLayer[] {
    const dispatch = useAppDispatch()
    const { flightId, direction, threshold, bounds } = useAppSelector(selectOrthoComparison)
    const [isHovering, setIsHovering] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const isDraggingRef = useRef(false)
    const rafRef = useRef<number | null>(null)
    // Keep latest direction/bounds in refs so drag handlers don't close over stale values
    // if the user changes direction or the bounds update while a drag is in progress.
    const directionRef = useRef(direction)
    directionRef.current = direction
    const boundsRef = useRef(bounds)
    boundsRef.current = bounds

    const isActive = Boolean(flightId) && threshold !== null && Boolean(bounds)

    // Restore map state whenever the divider goes inactive — comparison toggled off, bounds
    // cleared, or full unmount — not just on flight change. Keying on isActive means the cleanup
    // also fires on the early-return path below, so a stale resize cursor or a disabled dragPan
    // can't get stuck when threshold/bounds clear (e.g. mid-drag) and the handlers stop running.
    useEffect(() => {
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
            // a module-level ref, not one owned by this render; reading it at cleanup is the point
            // oxlint-disable-next-line react-hooks/exhaustive-deps
            getGlobalMapRef()?.current?.getMap()?.dragPan.enable()
            _comparisonHoverCursor = null
            isDraggingRef.current = false
        }
    }, [isActive])

    // Mirrors isActive, but written out so TS narrows threshold/bounds for the code below.
    if (!flightId || threshold === null || !bounds) return []

    const isVertical = direction === "left" || direction === "right"

    const source: [number, number] = isVertical
        ? [threshold, bounds.minLat]
        : [bounds.minLng, threshold]
    const target: [number, number] = isVertical
        ? [threshold, bounds.maxLat]
        : [bounds.maxLng, threshold]

    const lineData = [{ path: [source, target] as [number, number][] }]

    const getMap = () => getGlobalMapRef()?.current?.getMap()

    const handleHover = (info: PickingInfo) => {
        if (!isDraggingRef.current) {
            const isVert = directionRef.current === "left" || directionRef.current === "right"
            const cur = isVert ? "col-resize" : "row-resize"
            setIsHovering(info.picked)
            _comparisonHoverCursor = info.picked ? cur : null
        }
    }

    const handleDragStart = (_info: PickingInfo) => {
        const isVert = directionRef.current === "left" || directionRef.current === "right"
        isDraggingRef.current = true
        setIsDragging(true)
        getMap()?.dragPan.disable()
        _comparisonHoverCursor = isVert ? "col-resize" : "row-resize"
    }

    const handleDrag = (info: PickingInfo) => {
        if (!info.coordinate) return
        const currentBounds = boundsRef.current
        if (!currentBounds) return
        const isVert = directionRef.current === "left" || directionRef.current === "right"
        const [lng, lat] = info.coordinate as [number, number]
        const raw = isVert ? lng : lat
        const min = isVert ? currentBounds.minLng : currentBounds.minLat
        const max = isVert ? currentBounds.maxLng : currentBounds.maxLat
        const clamped = Math.min(max, Math.max(min, raw))
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null
            dispatch(setOrthoComparisonThreshold(clamped))
        })
    }

    const handleDragEnd = (_info: PickingInfo) => {
        isDraggingRef.current = false
        setIsDragging(false)
        setIsHovering(false)
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
        }
        getMap()?.dragPan.enable()
        _comparisonHoverCursor = null
    }

    const lineAlpha = isHovering && !isDragging ? 255 : 90

    return [
        new PathLayer({
            id: "ortho-comparison-divider-casing",
            data: lineData,
            getPath: (d: { path: [number, number][] }) => d.path,
            getColor: [90, 90, 90, lineAlpha],
            getWidth: 2,
            widthUnits: "pixels",
            pickable: false,
            extensions: [...getGlobalTerrainData().extensions],
        }),
        new PathLayer({
            id: "ortho-comparison-divider-visual",
            data: lineData,
            getPath: (d: { path: [number, number][] }) => d.path,
            getColor: [255, 255, 255, lineAlpha],
            getWidth: 2,
            widthUnits: "pixels",
            pickable: false,
            getDashArray: [8, 6],
            dashJustified: true,
            extensions: [
                new PathStyleExtension({ dash: true }),
                ...getGlobalTerrainData().extensions,
            ],
        }),
        // Wide transparent hit area — handles hover cursor and drag
        new PathLayer({
            id: "ortho-comparison-divider-hit",
            data: lineData,
            getPath: (d: { path: [number, number][] }) => d.path,
            getColor: [0, 0, 0, 0],
            getWidth: 16,
            widthUnits: "pixels",
            pickable: true,
            onHover: handleHover,
            onDragStart: handleDragStart,
            onDrag: handleDrag,
            onDragEnd: handleDragEnd,
            extensions: [...getGlobalTerrainData().extensions],
        }),
    ]
}
